import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import SiteCard from './SiteCard'
import Header from '@components/header/Header'

import useBreakpoint from '@utils/useBreakpoints'
import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'

import api from '@api/api'
import logger from '@utils/logger'

import { AuthScope } from '@api/enum'

interface Props {
  status: Status
}

type StatMapping = { [siteId: string]: IncidentsStatistiquesResponse }

const MySitesScreen = ({ status }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const sites = useReducer(SiteStore.store, (s) => s.sites)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const [stats, setStats] = React.useState<StatMapping>({})

  const onSiteSelected = (site: SiteResponse) => {
    SiteStore.actions.setSite(site)
  }

  React.useEffect(() => {
    if (scopes.includes(AuthScope.StatistiquesSiteLecture)) {
      Promise.all(sites.map((s) => api.incident.stats(s.id)))
        .then((sts) => {
          const mapping = sts.reduce((acc, cur, idx) => ({ ...acc, [sites[idx].id]: cur }), {} as StatMapping)
          setStats(mapping)
        })
        .catch(logger.error)
    }
  }, [sites])

  const renderSite = (site: SiteResponse) => (
    <SiteContainer key={site.id}>
      <SiteCard
        site={site}
        onSiteClicked={() => onSiteSelected(site)}
        stats={stats[site.id]}
        displayChecklistActions={scopes.includes(AuthScope.BandeauPenseBeteChecklistLecture)}
      />
    </SiteContainer>
  )

  return (
    <Root
      loading={status === 'loading'}
      renderSubHeader={() => <Header title={i18n.t('screens.home.mySites')} />}
      header={isPhone ? ' ' : i18n.t('screens.home.mySites')}>
      {!isPhone && <Header title={i18n.t('screens.home.mySites')} />}

      <MainContainer>
        <SectionTitle title={i18n.t('screens.home.mySites')} />
        <Container>
          {status === 'fetched' ? (
            sites.length > 0 ? (
              sites.map(renderSite)
            ) : (
              <MissingInfo>{i18n.t('screens.home.noSite')}</MissingInfo>
            )
          ) : (
            <MissingInfo>{i18n.t('common.error')}</MissingInfo>
          )}
        </Container>
      </MainContainer>
    </Root>
  )
}

export default MySitesScreen

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '24px 16px' : '24px')};
`
const Container = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`}
  padding: 16px 0;
`
const SiteContainer = styled(View)`
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? '0px 0px 16px 0px' : '0px 16px 16px 0px')};
`
const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;
          `
      : `padding: 16px 0px;`}
`
